import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, RefreshCw, ChevronDown, Plus, Rows3, Sparkles } from "lucide-react";
import { T, TONES, MANROPE, PRODUCTS, DENSITIES, GRID, FOCUS } from "./clientsTokens";
import { ClientRow } from "./ClientRow";
import { ClientFormDialog, TouchDialog, ImportNotesDialog } from "./ClientDialogs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CS = `${API}/customer-success`;
const PAGE = 25;
const DENSITY_KEY = "equipr_clients_density";
const INACTIVE_KEY = "equipr_clients_show_inactive";

const COLUMNS = [
  { key: "company_name", label: "Client", sortable: true, align: "left", strong: true },
  { key: "entitlements", label: "Entitlements", sortable: false, align: "left" },
  { key: "last_health_score", label: "Health", sortable: true, align: "right" },
  { key: "last_activity_date", label: "Activity", sortable: true, align: "right" },
  { key: "last_check_date", label: "Last check", sortable: true, align: "right" },
  { key: "next_check_date", label: "Next due", sortable: true, align: "right" },
  { key: "notes", label: "Notes & actions", sortable: false, align: "left" },
];

const Segment = ({ band, total, active, onClick }) => {
  const tone = TONES[band.tone] || TONES.neutral;
  return (
    <button onClick={onClick} title={`${band.label} — ${band.count} of ${total}`}
            data-testid={`band-seg-${band.key}`}
            style={{ flexGrow: Math.max(band.count, 0), flexBasis: 0, minWidth: 8, height: 22,
                     background: tone.dot, border: `1px solid ${tone.border}`,
                     boxShadow: active ? "inset 0 0 0 2px #1A1A1A" : "none",
                     transition: "box-shadow 150ms ease-out" }} />
  );
};

const LegendChip = ({ band, active, onClick }) => {
  const tone = TONES[band.tone] || TONES.neutral;
  return (
    <button onClick={onClick} data-testid={`band-chip-${band.key}`}
            className={`inline-flex items-center gap-1.5 whitespace-nowrap ${FOCUS}`}
            style={{ flex: "0 0 auto", height: 26, padding: "0 9px", borderRadius: 999,
                     background: active ? T.accentTint : "#fff",
                     border: `1px solid ${active ? T.accentEdge : T.border}` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.dot }} />
      <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.ink }}>{band.label}</span>
      <span className="mono text-[11px]" style={{ color: T.ink2 }}>{band.count}</span>
    </button>
  );
};

const HealthSummary = ({ summary, healthBand, checkBand, setHealthBand, setCheckBand }) => {
  const any = healthBand || checkBand;
  const block = (label, note, bands, active, set) => (
    <div className="min-w-0 flex flex-col gap-2.5">
      <div>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: T.muted }}>{label}</span>
        <span className="ml-2 text-[11.5px]" style={{ color: T.ink2 }}>{note}</span>
      </div>
      <div className="flex" style={{ gap: 2, borderRadius: 5, overflow: "hidden" }}>
        {bands.map((b) => (
          <Segment key={b.key} band={b} total={summary.total} active={active === b.key}
                   onClick={() => set(active === b.key ? null : b.key)} />
        ))}
      </div>
      <div className="flex flex-wrap" style={{ gap: 6 }}>
        {bands.map((b) => (
          <LegendChip key={b.key} band={b} active={active === b.key}
                      onClick={() => set(active === b.key ? null : b.key)} />
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
                  padding: "14px 16px 13px", boxShadow: "0 1px 2px rgba(26,26,26,0.04)" }}
         data-testid="client-health-summary">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-[14px] font-bold" style={{ ...MANROPE, color: T.ink }}>Client health</span>
        <span className="text-[11.5px]" style={{ color: T.ink2 }}>Click a band to filter the register. Health and checks filter together.</span>
        {any && (
          <button onClick={() => { setHealthBand(null); setCheckBand(null); }} data-testid="clear-band-filters"
                  className="ml-auto text-[12px] underline" style={{ color: T.accentInk }}>Clear band filters</button>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: "14px 28px" }}>
        {block("Health score", "Latest recorded score", summary.health_bands, healthBand, setHealthBand)}
        {block("Check cadence", "Next check against today", summary.check_bands, checkBand, setCheckBand)}
      </div>
    </div>
  );
};

export default function ClientsRegister({ onOpenClient }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState("");
  const [csm, setCsm] = useState("all");
  const [tier, setTier] = useState("all");
  const [sort, setSort] = useState({ key: "last_activity_date", dir: "desc" });
  const [products, setProducts] = useState([]);
  const [healthBand, setHealthBand] = useState(null);
  const [checkBand, setCheckBand] = useState(null);
  const [showInactive, setShowInactive] = useState(() => localStorage.getItem(INACTIVE_KEY) === "1");
  const [density, setDensity] = useState(() => localStorage.getItem(DENSITY_KEY) || "comfortable");
  const [selected, setSelected] = useState([]);
  const [menu, setMenu] = useState(null);
  const [page, setPage] = useState(1);
  const [syncOpen, setSyncOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [ask, setAsk] = useState("");
  const [asking, setAsking] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [dialog, setDialog] = useState({ kind: null, client: null });

  const load = useCallback(async (silent) => {
    if (!silent) setData(null);
    setError(null);
    try {
      const r = await axios.get(`${CS}/clients/register`, { params: { include_inactive: true } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load the client register.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const height = (DENSITIES.find((d) => d.key === density) || DENSITIES[1]).h;
  const cycleDensity = () => {
    const i = DENSITIES.findIndex((d) => d.key === density);
    const next = DENSITIES[(i + 1) % DENSITIES.length].key;
    setDensity(next); localStorage.setItem(DENSITY_KEY, next);
  };
  const toggleInactive = () => {
    const next = !showInactive;
    setShowInactive(next); localStorage.setItem(INACTIVE_KEY, next ? "1" : "0");
  };

  const all = data?.clients || [];
  const summary = data?.summary;
  // the population the register is showing — the footer and search count share it
  const population = showInactive ? all : all.filter((c) => c.active !== false);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const rows = all.filter((c) => {
      if (!showInactive && c.active === false) return false;
      if (needle && ![c.company_name, c.client_id, c.jira_org_name]
        .some((v) => (v || "").toLowerCase().includes(needle))) return false;
      if (csm !== "all" && c.csm !== csm) return false;
      if (tier === "vip" && !c.is_vip) return false;
      if (tier === "sla" && !(c.has_sla && !c.is_vip)) return false;
      if (tier === "standard" && (c.is_vip || c.has_sla)) return false;
      if (healthBand && c.health_band !== healthBand) return false;
      if (checkBand && c.check_band !== checkBand) return false;
      if (products.length && !products.every((p) => (c.products || []).includes(p))) return false;
      return true;
    });
    const dir = sort.dir === "asc" ? 1 : -1;
    return rows.sort((a, b) => {
      const av = a[sort.key], bv = b[sort.key];
      if (av === bv) return (a.company_name || "").localeCompare(b.company_name || "");
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return (av > bv ? 1 : -1) * dir;
    });
  }, [all, q, csm, tier, healthBand, checkBand, products, showInactive, sort]);

  useEffect(() => { setPage(1); }, [q, csm, tier, healthBand, checkBand, products, showInactive]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE));
  const rows = filtered.slice((page - 1) * PAGE, page * PAGE);
  const filtering = !!(q.trim() || csm !== "all" || tier !== "all" || healthBand || checkBand || products.length);
  const selectedClients = all.filter((c) => selected.includes(c.client_id));

  const clearFilters = () => {
    setQ(""); setCsm("all"); setTier("all"); setHealthBand(null); setCheckBand(null); setProducts([]);
  };

  const sortBy = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: "desc" }));

  const setInactive = async (c, value) => {
    try {
      await axios.put(`${CS}/clients/${encodeURIComponent(c.client_id)}`, { active: value });
      toast.success(value ? `${c.company_name} is active again` : `${c.company_name} marked inactive`);
      load(true);
    } catch (e) { toast.error("Could not change that"); }
  };

  const rowAction = (key, c) => {
    if (key === "open") return onOpenClient(c);
    if (key === "runbook") return onOpenClient(c, "runbook");
    if (key === "jira") {
      if (!c.jira_org_name) { toast.info("No Jira organisation is linked to this client"); return; }
      window.open(`https://equipr.atlassian.net/jira/servicedesk/projects/SUPPORT/queues`, "_blank", "noopener");
      return;
    }
    if (key === "inactive") return setInactive(c, c.active === false);
    if (key === "health") { window.location.href = `/health-check/${c.client_id}/staff`; return; }
    if (key === "touch") return setDialog({ kind: "touch", client: c });
    if (key === "edit") return setDialog({ kind: "edit", client: c });
  };

  const exportCsv = () => {
    const list = selectedClients.length ? selectedClients : filtered;
    const head = ["Client ID", "Client", "Jira org", "CSM", "Health", "Last check", "Next due", "Days late", "Entitlements"];
    const body = list.map((c) => [c.client_id, c.company_name, c.jira_org_name || "", c.csm,
      c.last_health_score ?? "", c.last_check_date || "", c.next_check_date || "",
      c.days_late || 0, (c.entitlements || []).join(" ")]);
    const csv = [head, ...body].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `equipr-clients-${data.today}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${list.length} clients`);
  };

  const submitAsk = async () => {
    if (!ask.trim()) return;
    setAsking(true); setAnswer(null);
    try {
      const r = await axios.post(`${CS}/clients/ask`, { question: ask.trim() });
      setAnswer(r.data.answer || r.data.response || "No answer came back.");
    } catch (e) { setAnswer(e?.response?.data?.detail || "That question could not be answered."); }
    finally { setAsking(false); }
  };

  const ctrl = `h-[34px] px-2.5 text-[12.5px] bg-white rounded-[8px] ${FOCUS}`;
  const ctrlBorder = { border: `1px solid ${T.control}`, color: T.ink };
  const menuItem = (label, hint, onClick, disabled, why) => (
    <button key={label} onClick={onClick} disabled={disabled} title={disabled ? why : undefined}
            data-testid={`sync-item-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className="w-full text-left px-2 py-2 rounded-lg flex gap-2 items-start disabled:cursor-not-allowed"
            style={disabled ? { background: "#E6E2D9" } : undefined}>
      <RefreshCw className="w-4 h-4 mt-[2px] shrink-0" style={{ color: disabled ? T.disabled : T.ink3 }} />
      <span>
        <span className="block text-[13px] font-semibold" style={{ color: disabled ? T.disabled : T.ink }}>{label}</span>
        <span className="block text-[11.5px]" style={{ color: disabled ? T.disabled : T.ink2 }}>{disabled ? why : hint}</span>
      </span>
    </button>
  );

  return (
    <div className="flex flex-col" style={{ gap: 14 }} data-testid="clients-register">
      {/* Header */}
      <div className="flex flex-wrap items-start gap-3">
        <div style={{ flex: "1 1 420px", minWidth: 0 }}>
          <h1 style={{ ...MANROPE, fontSize: 25, fontWeight: 800, letterSpacing: "-0.018em", color: T.ink }}
              data-testid="clients-title">Clients</h1>
          <p className="text-[13px] leading-[1.6]" style={{ color: T.ink2 }} data-testid="clients-subtitle">
            Customer Success. <span className="mono text-[11.5px]" style={{ color: T.ink }}>{summary?.total ?? "—"}</span> clients,{" "}
            <span className="mono text-[11.5px]" style={{ color: T.ink }}>{summary?.checks_overdue ?? "—"}</span> health checks overdue,{" "}
            <span className="mono text-[11.5px]" style={{ color: T.ink }}>{summary?.below_7 ?? "—"}</span> scored below 7.
          </p>
        </div>
        <button onClick={() => setDialog({ kind: "add", client: null })} data-testid="clients-add"
                className={`h-[34px] px-3 rounded-[7px] inline-flex items-center gap-1.5 whitespace-nowrap ${FOCUS}`}
                style={{ flex: "0 0 auto", background: T.accent, border: `1px solid ${T.accentEdge}`,
                         fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: T.ink }}>
          <Plus className="w-4 h-4" /> Add client
        </button>
        <div className="relative" style={{ flex: "0 0 auto" }}>
          <button onClick={() => setSyncOpen((o) => !o)} data-testid="clients-sync"
                  className={`h-[34px] px-3 rounded-[7px] inline-flex items-center gap-1.5 whitespace-nowrap bg-white ${FOCUS}`}
                  style={{ border: `1px solid ${T.control}`, fontSize: 12.5, color: T.ink }}>
            <RefreshCw className="w-4 h-4" style={{ color: T.ink3 }} /> Sync &amp; import
            <ChevronDown className="w-3.5 h-3.5" style={{ color: T.ink3 }} />
          </button>
          {syncOpen && (
            <>
              <div className="fixed inset-0" style={{ zIndex: 20 }} onClick={() => setSyncOpen(false)} />
              <div className="absolute right-0 top-[38px] p-1.5" data-testid="clients-sync-menu"
                   style={{ width: 300, zIndex: 30, background: T.panel, border: `1px solid ${T.border}`,
                            borderRadius: 12, boxShadow: "0 12px 32px rgba(26,26,26,0.16)" }}>
                {menuItem("Refresh client data", `Re-pulls Jira organisations, tickets and activity for ${summary?.total ?? 0} clients`,
                  () => { setSyncOpen(false); load(true); toast.success("Client data refreshed"); })}
                {menuItem("Import meeting notes", "Match notes to clients before anything is saved",
                  () => { setSyncOpen(false); setDialog({ kind: "import", client: null }); })}
                {menuItem("Import CSV", "Map columns before committing records", undefined, true,
                  "Not available yet — CSV import is not live")}
                {menuItem("Sync entitlements", "Updates product and SLA flags from billing", undefined, true,
                  "Not available yet — there is no billing connection")}
              </div>
            </>
          )}
        </div>
      </div>

      {summary && <HealthSummary summary={summary} healthBand={healthBand} checkBand={checkBand}
                                setHealthBand={setHealthBand} setCheckBand={setCheckBand} />}

      {/* Register shell */}
      <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12,
                    overflow: "hidden", boxShadow: "0 1px 2px rgba(26,26,26,0.04)" }}>
        {/* Toolbar */}
        <div style={{ padding: "13px 14px", borderBottom: `1px solid ${T.dividerInner}`, display: "flex", flexDirection: "column", gap: 10 }}>
          <div className="flex items-center flex-wrap" style={{ gap: 10 }}>
            <div className="relative" style={{ flex: 1, minWidth: 280, maxWidth: 520 }}>
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.muted }} />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="clients-search"
                     placeholder="Client name, client ID or Jira organisation"
                     className={`w-full h-[42px] pl-9 pr-20 text-[13.5px] rounded-[9px] ${FOCUS}`}
                     style={{ border: `1.5px solid ${T.ink}`, color: T.ink }} />
              <span className="mono absolute right-3 top-1/2 -translate-y-1/2 text-[10.5px]" style={{ color: "#8F8A7F" }}
                    data-testid="clients-search-count">
                {filtering ? `${filtered.length} of ${population.length}` : population.length}
              </span>
            </div>
            <select value={csm} onChange={(e) => setCsm(e.target.value)} className={ctrl} style={ctrlBorder} data-testid="clients-csm">
              <option value="all">All CSMs</option>
              {(data?.csms || []).map((n) => <option key={n} value={n}>{n}</option>)}
              <option value="Unassigned">Unassigned</option>
            </select>
            <select value={tier} onChange={(e) => setTier(e.target.value)} className={ctrl} style={ctrlBorder} data-testid="clients-tier">
              <option value="all">All tiers</option>
              <option value="vip">VIP</option>
              <option value="sla">SLA</option>
              <option value="standard">Standard</option>
            </select>
            <select value={`${sort.key}:${sort.dir}`} data-testid="clients-sort" className={ctrl} style={ctrlBorder}
                    onChange={(e) => { const [key, dir] = e.target.value.split(":"); setSort({ key, dir }); }}>
              <option value="last_activity_date:desc">Most recent activity</option>
              <option value="next_check_date:asc">Next check soonest</option>
              <option value="last_health_score:asc">Lowest health</option>
              <option value="company_name:asc">Client name</option>
            </select>
          </div>

          <div className="flex items-center flex-wrap" style={{ gap: 8 }}>
            <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.09em", textTransform: "uppercase", color: T.muted }}>Products</span>
            {PRODUCTS.map((p) => {
              const on = products.includes(p.key);
              return (
                <button key={p.key} title={`${p.name} — show clients entitled to ${p.key}`}
                        data-testid={`clients-product-${p.key}`}
                        onClick={() => setProducts((prev) => on ? prev.filter((x) => x !== p.key) : [...prev, p.key])}
                        className={`mono whitespace-nowrap ${FOCUS}`}
                        style={{ flex: "0 0 auto", height: 28, minWidth: 38, padding: "0 9px", borderRadius: 999, fontSize: 11,
                                 background: on ? T.accentTint : "#fff",
                                 border: `1px solid ${on ? T.accentEdge : T.border}`,
                                 fontWeight: on ? 700 : 500, color: T.ink }}>{p.key}</button>
              );
            })}
            <span style={{ width: 1, height: 18, background: T.dividerInner }} />
            <button onClick={toggleInactive} data-testid="clients-show-inactive"
                    className={`inline-flex items-center gap-2 ${FOCUS}`}>
              <span style={{ width: 34, height: 18, borderRadius: 999, position: "relative",
                             background: showInactive ? T.accent : T.pressed,
                             border: `1px solid ${showInactive ? T.accentEdge : T.control}`,
                             transition: "background 150ms ease-out" }}>
                <span style={{ position: "absolute", top: 2, left: showInactive ? 18 : 2, width: 12, height: 12,
                               borderRadius: 999, background: "#fff", transition: "left 150ms ease-out" }} />
              </span>
              <span className="text-[12px]" style={{ color: T.ink }}>Show inactive</span>
              <span className="mono text-[11px] px-1.5 rounded" style={{ background: T.inset, color: T.ink2 }}>{summary?.inactive ?? 0}</span>
            </button>
            <button onClick={cycleDensity} data-testid="clients-density"
                    className={`ml-auto h-[30px] px-2.5 rounded-[7px] inline-flex items-center gap-1.5 text-[12px] hover:bg-[#F4F2ED] ${FOCUS}`}
                    style={{ color: T.ink2 }}>
              <Rows3 className="w-3.5 h-3.5" /> {(DENSITIES.find((d) => d.key === density) || DENSITIES[1]).label}
            </button>
          </div>
        </div>

        {/* Ask strip */}
        <div style={{ background: T.sunken, borderBottom: `1px solid ${T.dividerInner}`, padding: "10px 14px" }} data-testid="clients-ask">
          <div className="flex items-center gap-2 flex-wrap">
            <Sparkles className="w-3.5 h-3.5" style={{ color: T.accentIcon }} />
            <span className="text-[12.5px] font-semibold" style={{ color: T.ink }}>Ask about your clients</span>
            <span className="text-[11.5px]" style={{ color: T.ink2 }}>Natural-language query across every client and their recent activity</span>
            <button onClick={() => setAskOpen((o) => !o)} className="ml-auto text-[12px] underline"
                    style={{ color: T.accentInk }} data-testid="clients-ask-toggle">{askOpen ? "Close" : "Open"}</button>
          </div>
          {askOpen && (
            <div className="mt-2.5">
              <div className="flex gap-2 flex-wrap">
                <input value={ask} onChange={(e) => setAsk(e.target.value)}
                       onKeyDown={(e) => { if (e.key === "Enter") submitAsk(); }}
                       placeholder="e.g. which clients had no touch in 30 days?" data-testid="clients-ask-input"
                       className={`flex-1 min-w-[260px] h-9 px-3 text-[13px] rounded-[8px] bg-white ${FOCUS}`}
                       style={{ border: `1px solid ${T.control}` }} />
                <button onClick={submitAsk} disabled={asking || !ask.trim()} data-testid="clients-ask-send"
                        className={`h-9 px-3 rounded-[8px] text-[12.5px] font-semibold bg-white ${FOCUS}`}
                        style={{ border: `1px solid ${T.control}`, color: T.ink }}>{asking ? "Asking…" : "Ask"}</button>
              </div>
              <div className="text-[11px] mt-1.5" style={{ color: T.ink3 }}>Answers read every client, not just the filtered list, and include each client's activity from the last 90 days.</div>
              {answer && <div className="mt-2 text-[13px] whitespace-pre-wrap p-3 rounded-[8px] bg-white"
                              style={{ border: `1px solid ${T.border}`, color: T.ink }} data-testid="clients-ask-answer">{answer}</div>}
            </div>
          )}
        </div>

        {/* Bulk bar */}
        {selected.length > 0 && (
          <div className="flex items-center flex-wrap" data-testid="clients-bulk-bar"
               style={{ background: T.accentTint, borderBottom: "1px solid #EFE0A0", padding: "9px 14px", gap: 10 }}>
            <span className="text-[12.5px] font-semibold" style={{ color: T.ink }}>
              {selected.length} {selected.length === 1 ? "client" : "clients"} selected
            </span>
            <span style={{ width: 1, height: 18, background: "#EFE0A0" }} />
            {[
              ["Assign CSM", null, "CSM ownership comes from the client's journey — set it on the journey"],
              ["Invite to a Health Check", () => { window.location.href = `/customer-success/health-checks?invite=${selectedClients.map((c) => c.client_id).join(",")}`; }],
              ["Log a touch", () => setDialog({ kind: "touch", client: null })],
              ["Export CSV", exportCsv],
              ["Bulk email", null, "Not available yet — bulk email endpoint is not live"],
              ["Mark inactive", async () => { for (const c of selectedClients) await setInactive(c, false); setSelected([]); }],
            ].map(([label, fn, why]) => (
              <button key={label} onClick={fn || undefined} disabled={!fn}
                      title={fn ? `${label} — applies to ${selected.length} ${selected.length === 1 ? "client" : "clients"}` : why}
                      data-testid={`bulk-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="h-7 px-2.5 rounded-[7px] text-[12px] font-semibold whitespace-nowrap disabled:cursor-not-allowed"
                      style={fn ? { background: "#fff", border: "1px solid #DCCF95", color: label === "Mark inactive" ? T.destructive : T.ink }
                                : { background: "#E6E2D9", border: "1px solid #D3CEC2", color: T.disabled }}>{label}</button>
            ))}
            <button onClick={() => setSelected([])} className="ml-auto text-[12px] underline" style={{ color: T.ink2 }}
                    data-testid="bulk-clear">Clear</button>
          </div>
        )}

        {/* Table */}
        <div style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 1280 }}>
            <div className="grid items-center" style={{ gridTemplateColumns: GRID, columnGap: 10, height: 36,
                          padding: "0 12px", background: T.sunken, borderBottom: `1px solid ${T.border}` }}>
              <input type="checkbox" data-testid="clients-select-all"
                     checked={rows.length > 0 && rows.every((r) => selected.includes(r.client_id))}
                     onChange={(e) => setSelected(e.target.checked ? rows.map((r) => r.client_id) : [])}
                     className="w-3.5 h-3.5 accent-[#1A1A1A] cursor-pointer" />
              {COLUMNS.map((col) => (
                <button key={col.key} onClick={col.sortable ? () => sortBy(col.key) : undefined}
                        data-testid={`clients-col-${col.key}`}
                        className={`whitespace-nowrap ${col.align === "right" ? "text-right" : "text-left"} ${col.sortable ? "hover:underline" : "cursor-default"}`}
                        style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
                                 color: col.strong ? T.ink : T.muted, fontWeight: col.strong ? 700 : 500 }}>
                  {col.label}
                  {sort.key === col.key && <span className="mono ml-1 text-[10px]">{sort.dir === "desc" ? "↓" : "↑"}</span>}
                </button>
              ))}
              <span />
            </div>

            {!data && !error && [...Array(6)].map((_, i) => (
              <div key={i} className="grid items-center" style={{ gridTemplateColumns: GRID, columnGap: 10, minHeight: height,
                            padding: "6px 12px", background: T.sunken, borderBottom: `1px solid ${T.dividerRow}` }}>
                <span />
                {[...Array(7)].map((__, j) => <div key={j} className="h-3.5 rounded animate-pulse" style={{ background: T.pressed }} />)}
                <span />
              </div>
            ))}

            {error && (
              <div style={{ padding: "56px 20px" }} className="text-center" data-testid="clients-error">
                <p className="text-[15px] font-semibold" style={{ color: T.ink }}>{error}</p>
                <button onClick={() => load()} className="mt-2 text-[12.5px] underline" style={{ color: T.accentInkPage }}>Try again</button>
                <div className="mono text-[11px] mt-1" style={{ color: T.ink3 }}>CS-REGISTER-LOAD</div>
              </div>
            )}

            {data && !error && rows.length === 0 && (
              <div style={{ padding: "56px 20px" }} className="text-center" data-testid="clients-empty">
                <p className="text-[15px] font-semibold" style={{ color: T.ink }}>
                  {all.length === 0 ? "No clients yet." : "No clients match these filters."}
                </p>
                <button onClick={all.length === 0 ? () => setSyncOpen(true) : clearFilters}
                        data-testid="clients-empty-action"
                        className="mt-3 h-[34px] px-3 rounded-[7px] text-[12.5px] bg-white"
                        style={{ border: `1px solid ${T.control}`, color: T.ink }}>
                  {all.length === 0 ? "Sync & import" : "Clear filters"}
                </button>
              </div>
            )}

            {data && rows.map((c) => (
              <ClientRow key={c.client_id} c={c} height={height} showInactive={showInactive}
                         selected={selected.includes(c.client_id)}
                         onSelect={(id) => setSelected((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id])}
                         onOpen={onOpenClient} onAction={rowAction}
                         menuOpen={menu === c.client_id} setMenu={setMenu} />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between flex-wrap gap-2"
             style={{ background: T.panel, borderTop: `1px solid ${T.dividerInner}`, padding: "11px 14px" }}>
          <span className="mono text-[11.5px]" style={{ color: T.ink2 }} data-testid="clients-footer-count">
            {filtered.length} of {population.length} clients · page {page}
          </span>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                    title={page === 1 ? "You are on the first page" : undefined} data-testid="clients-prev"
                    className="h-7 px-2.5 rounded-[7px] text-[12px] bg-white disabled:cursor-not-allowed"
                    style={{ border: `1px solid ${T.control}`, color: page === 1 ? T.disabled : T.ink }}>Previous</button>
            <span className="mono text-[11.5px]" style={{ color: T.ink2 }}>{page} / {pages}</span>
            <button onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
                    data-testid="clients-next"
                    className="h-7 px-2.5 rounded-[7px] text-[12px] bg-white disabled:cursor-not-allowed"
                    style={{ border: `1px solid ${T.control}`, color: page >= pages ? T.disabled : T.ink }}>Next</button>
          </div>
        </div>
      </div>

      <ClientFormDialog open={dialog.kind === "add" || dialog.kind === "edit"} client={dialog.kind === "edit" ? dialog.client : null}
                        onOpenChange={(o) => !o && setDialog({ kind: null })} onSaved={() => load(true)} />
      <TouchDialog open={dialog.kind === "touch"} today={data?.today}
                   clients={dialog.client ? [dialog.client] : selectedClients}
                   onOpenChange={(o) => !o && setDialog({ kind: null })} onSaved={() => load(true)} />
      <ImportNotesDialog open={dialog.kind === "import"} clients={all} today={data?.today}
                         onOpenChange={(o) => !o && setDialog({ kind: null })} onSaved={() => load(true)} />
    </div>
  );
}
